import { Avatar, Icon, IconNames, Tooltip } from '@dixa/lib-shared-habla';
import { boundMethod } from 'autobind-decorator';
import classNames from 'classnames';
import { accessControl } from 'lib-shared-access-control';
import { Presence } from 'lib-shared-legacy-types';
import { ampli } from 'lib-shared-tracking';
import { Component } from 'react';

import { AwayStatus } from 'apis/presence';
import { OrganizationSwitcher } from 'app/views/layout/components/layoutContainer/components/topBar/components/organizationSwitcher/organizationSwitcher';
import { CommandKeybindingsTooltipContent } from 'components/commandKeybindingsTooltipContent';
import { getMetaWorkingMedia } from 'utils/channels/media';
import { featureFlags } from 'utils/featureFlagging/featureFlags';
import { pubSub } from 'utils/pubSub';

import { numberFormatterInternational } from '../../../app/utils/telephonyHelpers';
import { buttonize } from '../../shared/util/buttonize';
import { ProfileProps } from '../containers/profile';

import MediaChannelBadges from './MediaChannelBadges';
import { PresenceDropdown } from './PresenceDropdown';
import styles from './profile.module.scss';

type PropsT = ProfileProps & {
  isSmall?: boolean;
  className?: string;
};

class Profile extends Component<PropsT> {
  @boundMethod
  changeYourStatus(location: 'topbar' | 'topbarDropdown'): void {
    if (this.props.presence.status === 'working') {
      this.props.presenceSetStatus({ status: 'away' }).then(() => {
        pubSub.publish('presenceAwayStatusSet');
        ampli.presenceUpdated({ status: 'away', location });
      });
    } else {
      this.props
        .presenceSetStatus({ status: 'working' })
        .then(() => ampli.presenceUpdated({ status: 'working', location }));
    }
  }

  @boundMethod
  toggleSimultaneousRing(location = 'topbar'): void {
    if (
      this.props.profile &&
      this.props.profile.phone_number &&
      typeof this.props.organization.trial_ended_at === 'string'
    ) {
      this.props
        .presenceSetSimultaneousRinging(
          !this.props.presence.presence_status.simultaneous_ringing,
        )
        .then((response) => {
          this.props.notificationRemoveByType('simring-on');
          if (response.data.enabled === true) {
            const phoneNumber = numberFormatterInternational({
              phoneNumber: this.props.profile.phone_number as string,
            });
            // Show notification that simultaneous ring is now enabled.
            const options = {
              type: 'simring-on',
              title: 'Simultaneous ring enabled',
              text: `You will now receive calls on the phone number ${phoneNumber}`,
              time: 10000,
            };
            this.props.notificationAdd(options);
          }

          ampli.simultaneousRingUpdated({
            enabled: response.data.enabled,
            location,
          });
        });
    }
  }

  getPresenceStatus(presence: Presence, numberOfAcw: number): string {
    if (presence.status !== 'offline' && numberOfAcw > 0) {
      return 'in wrap-up time';
    }

    if (['working', 'away', 'offline'].includes(presence.status)) {
      return presence.status;
    }

    return 'offline';
  }

  @boundMethod
  setCustomAwayStatus(
    awayStatus: Pick<AwayStatus, 'id' | 'label' | 'emoji'>,
  ): void {
    ampli.agentCustomAwayStatusUpdated({
      statusEmoji: awayStatus.emoji,
      statusLabel: awayStatus.label,
    });
    this.props.presenceSetStatus({
      status: 'away',
      custom_away_status_id: awayStatus.id,
    });
  }

  getOppositePresenceStatus(presence: Presence): string {
    switch (presence.status) {
      case 'working':
        return 'away';
      case 'away':
        return 'working';
      case 'offline':
      default:
        return 'online';
    }
  }

  getIconName(presence: Presence, numberOfAcw: number): IconNames {
    if (presence.status !== 'offline' && numberOfAcw > 0) {
      return 'clock';
    }

    return 'away-toggle';
  }

  render() {
    const flags = featureFlags.get(['organizations-sidebar']);
    const hasAccess = accessControl.hasAccess('presence.working_media');
    const { hasPhoneChannel, organization, presence, presenceMeta, profile } =
      this.props;
    const numberOfAcw =
      this.props.presence.presence_status.load_descriptions.filter(
        ({ load_type }) => load_type === 'acw',
      ).length;

    const isInTrial =
      typeof organization.trial_ended_at === 'string' ? false : true;

    const isValidPhoneNumber = !!this.props.profile?.phone_number;
    const presenceStatus = this.getPresenceStatus(presence, numberOfAcw);
    const oppositePresenceStatus = this.getOppositePresenceStatus(presence);
    const iconName: IconNames = this.getIconName(presence, numberOfAcw);

    const metaWorkingMedia = getMetaWorkingMedia(presence.working_channels);

    const presenceIconColor =
      presence.status === 'working'
        ? 'var(--dixa-color-green-400)'
        : 'var(--dixa-color-orange-400)';

    const presenceTooltipContent = (
      <CommandKeybindingsTooltipContent commandName="app:togglePresence">
        You are {presenceStatus}. Click to set {oppositePresenceStatus}
      </CommandKeybindingsTooltipContent>
    );

    return (
      <div className={styles.root} tabIndex={0}>
        {flags.organizationsSidebar && <OrganizationSwitcher />}

        <div className={styles.avatar}>
          {this.props.isSmall && (
            <span
              className={classNames(styles.avatarBadge, {
                [styles.avatarBadgeWorking]:
                  this.props.presence.status === 'working',
                [styles.avatarBadgeAway]: this.props.presence.status === 'away',
              })}
            />
          )}

          <Avatar user={profile} size="32" />
        </div>

        {!this.props.isSmall && (
          <div className={styles.toggles}>
            <Tooltip content={presenceTooltipContent} wrap="none">
              <div
                {...buttonize(() => this.changeYourStatus('topbar'))}
                className={classNames(styles.toggle, {
                  [styles.toggleAnimated]:
                    presenceMeta.setAsyncStatus === 'loading',
                })}
              >
                <Icon name={iconName} size={24} color={presenceIconColor} />
              </div>
            </Tooltip>

            {metaWorkingMedia.map((metaMedia) => (
              <MediaChannelBadges
                key={metaMedia.name}
                presenceMeta={presenceMeta}
                hasAccess={hasAccess}
                telephony={this.props.telephony}
                presence={this.props.presence}
                presenceSetMedia={this.props.presenceSetMedia}
                errorMicrophoneErrorShowModal={
                  this.props.errorMicrophoneErrorShowModal
                }
                personalToggleRememberMicrophoneAccess={
                  this.props.personalToggleRememberMicrophoneAccess
                }
                metaMedia={metaMedia}
              />
            ))}
          </div>
        )}

        <PresenceDropdown
          presence={this.props.presence}
          profile={profile}
          metaWorkingMedia={metaWorkingMedia}
          telephony={this.props.telephony}
          setCustomAwayStatus={this.setCustomAwayStatus}
          presenceSetMedia={this.props.presenceSetMedia}
          presenceSetMediaChannel={this.props.presenceSetMediaChannel}
          errorMicrophoneErrorShowModal={
            this.props.errorMicrophoneErrorShowModal
          }
          personalToggleRememberMicrophoneAccess={
            this.props.personalToggleRememberMicrophoneAccess
          }
          validPhoneNumber={isValidPhoneNumber}
          isInTrial={isInTrial}
          isPresenceStatusLoading={presenceMeta.setAsyncStatus === 'loading'}
          toggleSimultaneousRing={this.toggleSimultaneousRing}
          hasAccess={hasAccess}
          hasPhoneChannel={hasPhoneChannel}
          changeYourStatus={this.changeYourStatus}
          oppositePresenceStatus={oppositePresenceStatus}
          showLogoutModal={this.props.showLogoutModal}
        />
      </div>
    );
  }
}

export default Profile;
